import { z } from 'zod';
import { RECORD_STATUSES, US_STATE_CODES } from '../enums';
import type { RecordStatus } from '../enums';
import { recordStatus } from './common';
import { registerReportDefs, reportColumn as c } from './reports';
import type { ReportDef } from './reports';
import type { UserRef } from './users';

/**
 * Notifications (reference PC677) and Campaigns (PC760).
 *
 * A notification is a message template tied to an event ("Enrollment", "Port created", ...).
 * When the event happens to a customer, the Active template of each channel is sent to them
 * and logged. The event names are the reference's catalogue (product inventory, appendix
 * part 4); the form fields and the %VARIABLE% names are those of its add forms (appendix
 * 12.6 and 12.7). No mail or SMS gateway is connected, so delivery is simulated.
 */

export const NOTIFICATION_CHANNELS = ['email', 'sms', 'ivr'] as const;
export type NotificationChannel = (typeof NOTIFICATION_CHANNELS)[number];

export const CHANNEL_LABEL: Record<NotificationChannel, string> = {
  email: 'Email',
  sms: 'SMS',
  ivr: 'IVR',
};

/** Email events of the reference ("Notification Type"). */
export const EMAIL_EVENTS = [
  'Account status changes',
  'Activation confirmations',
  'Autopay Cancelled',
  'AutoPay CC Declined',
  'Autopay Failure',
  'Autopay Payment Will Be Processed',
  'AutoPay Processed',
  'Autopay Set Up',
  'Bulk SIM Purchase',
  'BYOD New activation Welcome letter',
  'BYOD New activation with sim purchase Welcome letter',
  'BYOD port Welcome letter',
  'BYOD port with sim purchase Welcome letter',
  'Cancel order',
  'Change Address',
  'Change Email',
  'Change Password',
  'Changed security information',
  'Credit Card expiring',
  'Customer Email Verification',
  'Customer Enrollment Tracking',
  'Disconnect',
  'E911 Registration Fail',
  'Enrollment',
  'Enrollment Status Approve',
  'Enrollment Status Reject',
  'Esim QR Code 4G',
  'Failed shipment',
  'Forgot password',
  'High Usage alert',
  'IR WALLET BALANCE EXHAUSTED',
  'IVR pin generate',
  'MRC payment failed',
  'MRC reminder',
  'Order Confirmation',
  'Payment confirmation',
  'Payment failed',
  'Payment processor account updater',
  'Plan Already Expired (Non Lifeline)',
  'Plan change next cycle',
  'Plan Expire (Prepaid Customer)',
  'Port activated',
  'Port Cancelled',
  'Port created',
  'Port failed',
  'Port in Resolution required',
  'Port Out Fail',
  'Port Out Request',
  'Port Out Success',
  'Port reminder 1 for inactive line',
  'Port reminder 2 for inactive line',
  'Port reminder 3 for inactive line',
  'Refund',
  'Renew Plan',
  'Schedule Disconnection',
  'Send Password Activate',
  'Service Credit',
  'Set new autopay',
  'Shipping notification For New Order',
  'Shipping notification For Website Customer',
  'SIM activated',
  'SIM Activation Fail',
  'SIM only purchase',
  'Sim swap completed',
  'Successful Payment: Plan',
  'Successful Payment: Top Up/Add-On',
  'Successful Payment: Wallet',
  'Ticket Notification',
  'Website Customer Enrollment Without Invoice',
  'Website Enrollment Status Approve',
] as const;

/** Events the reference offers for SMS only. */
export const SMS_ONLY_EVENTS = [
  'Data throttling due to monthly plan data limit reached',
  'Data top up added',
  'Data top up needed',
  'Data usage 75%',
  'Data usage 90%',
  'Data usage 100%',
  'ILD turned off due to 0 ILD flex wallet balance',
  'International Long Distance top-up needed',
  'Wallet Feature added for International Long Distance',
  'Wallet Max limit reached for data top-up',
  'Welcome letter Reminder',
] as const;

/**
 * Events offered for a channel. The reference lists 57 SMS events; which of the email events
 * they are was not recorded, so SMS and IVR offer the email events plus the SMS-only ones.
 */
export const eventsFor = (channel: NotificationChannel): readonly string[] =>
  channel === 'email' ? EMAIL_EVENTS : [...EMAIL_EVENTS, ...SMS_ONLY_EVENTS];

/** Events this application raises by itself today. */
export const RAISED_EVENTS = [
  'Enrollment',
  'SIM activated',
  'Account status changes',
  'Disconnect',
  'Sim swap completed',
  'Change Address',
  'Shipping notification For New Order',
  'Port created',
  'Port activated',
  'Port Cancelled',
  'Port failed',
  'Successful Payment: Wallet',
  'Successful Payment: Top Up/Add-On',
  'Plan change next cycle',
  // Raised since 2026-10-21, once the release switch `notification-events` is on (LATER_EVENTS).
  'Autopay Set Up',
  'Autopay Cancelled',
  'AutoPay Processed',
  'Autopay Failure',
  'Renew Plan',
  'Successful Payment: Plan',
  'Cancel order',
  'Change Email',
  'IVR pin generate',
  'Port Out Request',
  'Port Out Success',
  'Port Out Fail',
  'Ticket Notification',
  'Data top up added',
  // Raised since 2026-10-30, once the release switch `payment-messages` is on (PAYMENT_EVENTS).
  'Payment confirmation',
  'Refund',
  // Raised since 2026-10-30, once the release switch `more-notification-events` is on.
  'Order Confirmation',
  'Activation confirmations',
  'Payment failed',
  'AutoPay CC Declined',
  'Esim QR Code 4G',
  // The customer portal's own (switch `customer-portal`).
  'Send Password Activate',
  'Forgot password',
  'Change Password',
] as const;
export type RaisedEvent = (typeof RAISED_EVENTS)[number];

/** Events raised after a deployment: sent only while the release switch `notification-events` is on. */
export const LATER_EVENTS: readonly RaisedEvent[] = [
  'Autopay Set Up',
  'Autopay Cancelled',
  'AutoPay Processed',
  'Autopay Failure',
  'Renew Plan',
  'Successful Payment: Plan',
  'Cancel order',
  'Change Email',
  'IVR pin generate',
  'Port Out Request',
  'Port Out Success',
  'Port Out Fail',
  'Ticket Notification',
  'Data top up added',
];

/** Sent only while the release switch `payment-messages` is on. */
export const PAYMENT_EVENTS: readonly RaisedEvent[] = ['Payment confirmation', 'Refund'];

/** Sent only while the release switch `more-notification-events` is on. */
export const MORE_EVENTS: readonly RaisedEvent[] = [
  'Order Confirmation',
  'Activation confirmations',
  'Payment failed',
  'AutoPay CC Declined',
  'Esim QR Code 4G',
];

/** Variables every notification may use, written as the reference writes them. */
export const TEMPLATE_VARIABLES = [
  '%ACCOUNT%',
  '%FIRST_NAME%',
  '%LAST_NAME%',
  '%MAIL_TELEPHONE%',
  '%COMPANY_NAME%',
  '%ENROLLMENT_ID%',
  '%EMAIL_ADDRESS%',
  '%COPYRIGHT_YEAR%',
  '%ADDRESS%',
  '%UNSUBSCRIBE_URL%',
] as const;

/**
 * Variables of single events, as read from the reference ("%STATUS_RECIEVED%" is its
 * spelling). Events that were not opened there have the common variables only.
 */
export const EVENT_VARIABLES: Record<string, readonly string[]> = {
  'Account status changes': ['%CURRENT_ACCOUNT_STATUS%', '%PREVIOUS_ACCOUNT_STATUS%'],
  Disconnect: ['%DISCONNECTION_DATE%', '%MDN%'],
  'Port created': ['%STATUS_RECIEVED%', '%ERROR_DESCRIPTION%', '%PORTIN_DATE_TIME%'],
  'Port activated': ['%STATUS_RECIEVED%', '%ERROR_DESCRIPTION%', '%PORTIN_DATE_TIME%', '%MDN%'],
  'Shipping notification For New Order': ['%ENROLLMENT_TRACKING_LINK%', '%TRACKING_NO%'],
  'Successful Payment: Wallet': ['%WALLET_PAYMENT%', '%TOTAL_WALLET_BALANCE%', '%PAYMENT_METHOD%'],
  'Sim swap completed': ['%MDN%'],
  'Autopay Cancelled': ['%MDN%', '%PLAN_NAME%'],
  // The amount without a dollar sign: the text writes its own.
  'Payment confirmation': ['%AMOUNT%'],
  'Payment failed': ['%AMOUNT%'],
  Refund: ['%REFUNDED_AMOUNT%'],
  'Successful Payment: Plan': [
    '%AMOUNT_PAID%',
    '%INVOICE_NUMBER%',
    '%PAID_DATE_TIME%',
    '%PLAN_NAME%',
  ],
  'Renew Plan': [
    '%AMOUNT_PAID%',
    '%INVOICE_NUMBER%',
    '%PAID_DATE_TIME%',
    '%PLAN_EXPIRY_DATE%',
    '%PLAN_NAME%',
  ],
  'Successful Payment: Top Up/Add-On': [
    '%TOPUP_ADDON_AMOUNT%',
    '%TOPUP_ADDON_DATE_TIME%',
    '%TOPUP_ADDON_NAME%',
  ],
  'Order Confirmation': ['%BILLING_ADDRESS%', '%ORDER_ID%', '%PAYMENT_DATE%'],
  'Autopay Set Up': ['%AUTOPAY_SETUP_DATE%', '%USED_CARD_LAST_4_DIGITS%'],
  'Port Out Fail': ['%FAIL_REASON%'],
  'Send Password Activate': ['%CUSTOMER_PASSWORD%'],
  'Forgot password': ['%CUSTOMER_PASSWORD%'],
  'Port failed': ['%STATUS_RECIEVED%', '%ERROR_DESCRIPTION%', '%PORTIN_DATE_TIME%'],
  'Esim QR Code 4G': ['%ACTIVATION_CODE%', '%ESIM%'],
  'Plan change next cycle': ['%MDN%', '%PLAN_NAME%'],
};

/** The variables offered under the text of a notification for an event. */
export const variablesFor = (eventName: string): string[] => [
  ...TEMPLATE_VARIABLES,
  ...(EVENT_VARIABLES[eventName] ?? []),
];

/** Variables of the Create Campaign form. */
export const CAMPAIGN_VARIABLES = [
  '%ACCOUNT%',
  '%COMPANY_NAME%',
  '%FIRST_NAME%',
  '%LAST_NAME%',
  '%MAIL_TELEPHONE%',
  '%SERVICE_ADDRESS%',
  '%MAILING_ADDRESS%',
  '%UNSUBSCRIBE_URL%',
  '%ENROLLMENT_ID%',
  '%ENCRYPTED_ENROLLMENT_ID%',
  '%MDN%',
  '%NON_USES_TRACKING_URL%',
  '%PLAN_RENEW_DATE%',
  '%CUSTOMER_PASSWORD%',
  '%CAMPAIGN_URL%',
  '%ADDRESS_CONFIRM_URL%',
] as const;

/** "Choose where to send SMS:" on the SMS notification form. */
export const SMS_TARGETS = ['Primary Number', 'Alternate Number', 'Both'] as const;
export type SmsTarget = (typeof SMS_TARGETS)[number];

const SMS_MAX = 918; // six concatenated GSM segments
const email = (label: string) =>
  z.string().trim().max(120).email(`${label} is not valid`).or(z.literal('')).default('');

export const notificationInputSchema = z
  .object({
    channel: z.enum(NOTIFICATION_CHANNELS),
    eventName: z.string().trim().min(1, 'Select Notification Type').max(100),
    categoryName: z.string().trim().max(60).default(''),
    /** Carrier codes the template is for; empty means every carrier. */
    carriers: z.array(z.string().trim().max(20)).max(50).default([]),
    /** States the template is for; empty means every state. */
    states: z.array(z.enum(US_STATE_CODES)).default([]),
    fromName: z.string().trim().max(80).default(''),
    fromEmail: email('From Email Address'),
    bccEmail: email('BCC Email Address'),
    replyTo: email('Reply To'),
    smsName: z.string().trim().max(80).default(''),
    subject: z.string().trim().max(200).default(''),
    body: z.string().trim().min(1, 'Message is required').max(10_000),
    status: recordStatus.default('Active'),
    /** SMS: the number of the customer the message goes to. */
    smsTarget: z.enum(SMS_TARGETS).default('Primary Number'),
    /**
     * "Restriction for child account". Stored as entered; there are no child accounts here,
     * so it changes nothing yet.
     */
    restrictChildAccount: z.enum(['Yes', 'No']).default('No'),
  })
  .superRefine((v, ctx) => {
    const need = (path: string, message: string) =>
      ctx.addIssue({ code: 'custom', path: [path], message });
    if (!eventsFor(v.channel).includes(v.eventName)) need('eventName', 'Select Notification Type');
    if (v.channel === 'email') {
      if (!v.fromEmail) need('fromEmail', 'From Email Address is required');
      if (!v.subject) need('subject', 'Subject is required');
    } else {
      if (v.channel === 'sms' && !v.smsName) need('smsName', 'SMS Name is required');
      if (v.body.length > SMS_MAX) need('body', `A message is at most ${SMS_MAX} characters`);
    }
  });
export type NotificationInput = z.input<typeof notificationInputSchema>;

export interface NotificationTemplateDto {
  id: string;
  channel: NotificationChannel;
  eventName: string;
  categoryName: string;
  carriers: string[];
  states: string[];
  fromName: string;
  fromEmail: string;
  bccEmail: string;
  replyTo: string;
  smsName: string;
  subject: string;
  body: string;
  status: RecordStatus;
  smsTarget: SmsTarget;
  restrictChildAccount: 'Yes' | 'No';
  createdAt: string;
  createdBy: UserRef | null;
  updatedAt: string;
  updatedBy: UserRef | null;
}

/** "Sample Email Notification": Customer ID* and To Email*; SMS: Customer ID, Telephone Number*. */
export const sampleSendSchema = z.object({
  customerId: z
    .string()
    .trim()
    .regex(/^\d{1,11}$/, 'Customer ID is required'),
  to: z.string().trim().min(1, 'Enter where to send the sample').max(120),
});
export type SampleSendInput = z.input<typeof sampleSendSchema>;

/** GSM-7 or Unicode, and how many SMS segments a text takes (the reference's counter). */
export function smsSegments(text: string): { unicode: boolean; length: number; segments: number } {
  // eslint-disable-next-line no-control-regex
  const unicode = /[^\u0000-\u007f£¥èéùìòÇØøÅåΔ_ΦΓΛΩΠΨΣΘΞÆæßÉÄÖÑÜ§¿äöñüà€]/.test(text);
  const length = text.length;
  const [single, multi] = unicode ? [70, 67] : [160, 153];
  return { unicode, length, segments: length <= single ? 1 : Math.ceil(length / multi) };
}

/* ------------------------------- Sent messages ----------------------------- */

/** Statuses of the reference's Resend Notification filter; this build produces Success and Failed. */
export const SENT_STATUSES = ['Success', 'Process', 'Pending', 'Failed', 'Cancelled'] as const;

export interface SentNotificationDto {
  id: string;
  channel: NotificationChannel;
  eventName: string;
  to: string;
  subject: string;
  message: string;
  status: (typeof SENT_STATUSES)[number];
  error: string;
  sentAt: string;
  sentBy: UserRef | null;
}

export const sentListQuerySchema = z.object({
  dateFrom: z
    .string()
    .regex(/^\d{4}-\d{2}-\d{2}$/)
    .optional(),
  dateTo: z
    .string()
    .regex(/^\d{4}-\d{2}-\d{2}$/)
    .optional(),
  channel: z.enum(['email', 'sms']).optional(),
  eventName: z.string().trim().max(100).optional(),
  status: z.enum(SENT_STATUSES).optional(),
});

/* --------------------------------- Campaigns ------------------------------- */

export const CAMPAIGN_TYPES = ['SMS', 'Email'] as const;
export type CampaignType = (typeof CAMPAIGN_TYPES)[number];

const BCC_EMAIL = /^[^\s@,]+@[^\s@,]+\.[^\s@,]+$/;

/**
 * Create Campaign: Campaign Name*, Campaign Type*, then the fields of that type. SMS has
 * Subject*, Message* and Status*; Email adds From Email Address*, From Email Name and BCC
 * Email Address (several, separated by commas).
 */
export const campaignInputSchema = z
  .object({
    name: z.string().trim().min(1, 'Campaign Name is required').max(100),
    type: z.enum(CAMPAIGN_TYPES, { required_error: 'Select Campaign Type' }),
    fromEmail: email('From Email Address'),
    fromName: z.string().trim().max(80).default(''),
    bccEmail: z.string().trim().max(500).default(''),
    // Checked below with the other fields of the type, so one answer names them all.
    subject: z.string().trim().max(200).default(''),
    body: z.string().trim().min(1, 'Message is required').max(10_000),
    status: recordStatus.default('Active'),
  })
  .superRefine((v, ctx) => {
    const need = (path: string, message: string) =>
      ctx.addIssue({ code: 'custom', path: [path], message });
    if (!v.subject) need('subject', 'Subject is required');
    if (v.type === 'Email') {
      if (!v.fromEmail) need('fromEmail', 'From Email Address is required');
      const bcc = v.bccEmail ? v.bccEmail.split(',').map((x) => x.trim()) : [];
      if (bcc.some((address) => !BCC_EMAIL.test(address)))
        need('bccEmail', 'BCC Email Address is not valid');
    } else if (v.body.length > SMS_MAX) need('body', `A message is at most ${SMS_MAX} characters`);
  });
export type CampaignInput = z.input<typeof campaignInputSchema>;

export interface CampaignDto {
  id: string;
  name: string;
  type: CampaignType;
  fromEmail: string;
  fromName: string;
  bccEmail: string;
  subject: string;
  body: string;
  status: RecordStatus;
}

/** "Customer Type" of the attach form: a CSV of customers, or every Active customer. */
export const CAMPAIGN_CUSTOMER_TYPES = ['External Upload CSV', 'All Active'] as const;
/**
 * "Message Type". It decides which of the customer's switches "Check Preference" looks at:
 * Stop Promotional or Stop Transactional messages.
 */
export const CAMPAIGN_MESSAGE_TYPES = ['Promotional', 'Transactional'] as const;

/**
 * "SetUp Campaign.": the fields sent beside the CSV file, whose header is "MDN, CUSTOMER
 * ID". A row names a customer by either column.
 */
export const campaignAttachSchema = z.object({
  campaignId: z.string().regex(/^[a-f\d]{24}$/i, 'Select Campaign'),
  customerType: z.enum(CAMPAIGN_CUSTOMER_TYPES).default('External Upload CSV'),
  messageType: z.enum(CAMPAIGN_MESSAGE_TYPES).default('Promotional'),
  checkPreference: z.enum(['Yes', 'No']).default('Yes'),
  alternateNumber: z.enum(['Yes', 'No']).default('No'),
  scheduleDate: z
    .string()
    .regex(/^\d{4}-\d{2}-\d{2}$/, 'Enter a valid date')
    .or(z.literal(''))
    .default(''),
});
export const CAMPAIGN_CSV_COLUMNS = ['MDN', 'CUSTOMER ID'] as const;

export const campaignCancelSchema = z.object({
  reason: z.string().trim().min(1, 'Cancel Reason is required').max(100),
});

/* ------------------------------- List screens ------------------------------ */

const AUDIT = [
  c('createdBy', 'Created By'),
  c('createdAt', 'Created Date', 'datetime'),
  c('updatedBy', 'Updated By'),
  c('updatedAt', 'Updated Date', 'datetime'),
];
const STATUS_FILTER = {
  type: 'select',
  key: 'status',
  label: 'Status',
  options: RECORD_STATUSES,
  emptyLabel: 'All',
} as const;

const list = (
  def: Omit<ReportDef, 'group' | 'name' | 'submitLabel' | 'autoRun' | 'resultHeading'>,
) =>
  ({
    ...def,
    group: 'screen',
    name: def.heading,
    submitLabel: 'Search',
    autoRun: true,
    resultHeading: null,
  }) satisfies ReportDef;

export const NOTIFICATION_LIST_KEY: Record<NotificationChannel, string> = {
  email: 'email-notifications',
  sms: 'sms-notifications',
  ivr: 'ivr-notifications',
};
/** Add / View permission of each channel. */
export const NOTIFICATION_CODES: Record<NotificationChannel, { add: string; view: string }> = {
  email: { add: 'PC668', view: 'PC669' },
  sms: { add: 'PC675', view: 'PC676' },
  ivr: { add: 'PC832', view: 'PC833' },
};

export const MESSAGING_SCREENS: readonly ReportDef[] = [
  list({
    key: 'email-notifications',
    heading: 'View Email Notifications',
    code: 'PC669',
    filters: [
      { type: 'text', key: 'eventName', label: 'Event Name', maxLength: 100 },
      { type: 'carrier', key: 'carrier', label: 'Carrier' },
      { type: 'state', key: 'state', label: 'State', allLabel: 'All' },
      STATUS_FILTER,
    ],
    columns: [
      c('eventName', 'Event Name'),
      c('categoryName', 'Category Name'),
      c('carrier', 'Carrier'),
      c('state', 'State'),
      c('scheduleType', 'Schedule Type'),
      c('fromEmail', 'From E-mail'),
      c('bccEmail', 'BCC E-mail'),
      c('status', 'Status'),
      ...AUDIT,
    ],
  }),
  list({
    key: 'sms-notifications',
    heading: 'View SMS Notifications',
    code: 'PC676',
    filters: [
      { type: 'text', key: 'eventName', label: 'Event Name', maxLength: 100 },
      { type: 'carrier', key: 'carrier', label: 'Carrier' },
      { type: 'state', key: 'state', label: 'State', allLabel: 'All' },
      STATUS_FILTER,
    ],
    columns: [
      c('eventName', 'Event Name'),
      c('categoryName', 'Category Name'),
      c('state', 'State'),
      c('smsName', 'SMS Name'),
      c('carrier', 'Carrier'),
      c('scheduleType', 'Schedule Type'),
      c('status', 'Status'),
      ...AUDIT,
    ],
  }),
  list({
    key: 'ivr-notifications',
    heading: 'View IVR Notification',
    code: 'PC833',
    filters: [
      { type: 'text', key: 'eventName', label: 'Event Name', maxLength: 100 },
      STATUS_FILTER,
    ],
    // The reference's IVR list columns were not read.
    columns: [c('eventName', 'Event Name'), c('body', 'Message'), c('status', 'Status'), ...AUDIT],
  }),
  list({
    key: 'campaigns',
    heading: 'View Campaign List',
    code: 'PC762',
    filters: [
      { type: 'text', key: 'q', label: 'Campaign Name / Subject', maxLength: 100 },
      {
        type: 'select',
        key: 'type',
        label: 'Campaign Type',
        options: ['EMAIL', 'SMS'],
        emptyLabel: 'All',
      },
      STATUS_FILTER,
    ],
    columns: [
      c('name', 'Campaign Name'),
      c('type', 'Campaign Type'),
      c('fromEmail', 'From E-mail'),
      c('subject', 'Subject'),
      c('status', 'Status'),
      ...AUDIT,
    ],
  }),
  list({
    key: 'campaign-batches',
    heading: 'Attach Customers To A Campaign',
    code: 'PC763',
    filters: [],
    columns: [
      c('batchId', 'Batch Id'),
      c('campaignName', 'Campaign Name'),
      c('campaignType', 'Campaign Type'),
      c('createdAt', 'Batch Created Date', 'datetime'),
      c('scheduleDate', 'Schedule Date', 'date'),
      c('successMdn', 'Success MDN', 'number'),
      c('failedMdn', 'Failed MDN', 'number'),
      c('sent', 'Successfully Sent', 'number'),
      c('pending', 'Pending', 'number'),
      c('failed', 'Failed', 'number'),
      c('processStatus', 'Process Status'),
      c('alternateNumber', 'Alternate Number'),
      c('checkPreference', 'Check Preference'),
    ],
  }),
];
registerReportDefs(MESSAGING_SCREENS);

/**
 * The SPF check of an e-mail notification's From address (walk of 2026-10-27; switch
 * `notification-spf-check`): "SPF record is valid" when the address's domain publishes an SPF
 * record. Assumption: the reference's exact test was not seen; here any `v=spf1` record counts.
 */
export const spfCheckSchema = z.object({
  email: z.string().trim().email('Enter a valid e-mail address').max(120),
});
export type SpfCheckInput = z.input<typeof spfCheckSchema>;

export interface SpfCheckDto {
  domain: string;
  valid: boolean;
  /** The SPF record found, as published. */
  record: string | null;
}
